<template>
  <div class="toast fade show mt-2" :class="getColor(color)">
    <div class="toast-header bg-transparent border-0">
      <span class="toast-moo-icon" :class="getAccentColor(color)">
        <i class="fas" :class="getIconClass(color)"></i>
      </span>
      <div class="toast-body toast-moo-body" :class="getTextColor(color)">{{ description }}</div>
      <span class="me-auto font-weight-bold" :class="getTextColor(color)">
        {{ title }}
      </span>
      <!-- <small :class="getTextColor(color)">{{ date }}</small> -->
      <i class="fas fa-times text-md ms-3 cursor-pointer toast-moo-close" :class="getTextColor(color)" @click="closeHandler" />
    </div>
    <!-- <hr class="horizontal dark m-0" :class="getHrColor(color)" /> -->
    <span class="toast-moo-bar" :class="getAccentColor(color)"></span>
  </div>
</template>

<script>
export default {
  name: "MaterialSnackbar",
  props: {
    title: {
      type: String,
      required: true,
    },
    date: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    icon: {
      type: Object,
      component: String,
      color: String,
      default: () => { },
    },
    color: {
      type: String,
      default: "success",
    },

    closeHandler: {
      type: Function,
      default: () => { },
    },
  },
  methods: {
    getColor: (color) => {
      let colorValue;

      if (color === "white") {
        colorValue = "bg-white";
      } else {
        colorValue = `bg-gradient-${color}`;
      }

      return colorValue;
    },
    getIcon: (iconColor) => (iconColor ? `text-${iconColor}` : null),
    getTextColor: (color) => (color === "white" ? "text-dark" : "text-white"),
    getHrColor: (color) => (color === "white" ? "dark" : "light"),
    getIconClass: (color) => {
      const icons = {
        success: "fa-check",
        danger: "fa-times",
        warning: "fa-exclamation",
        info: "fa-info",
      };
      return icons[color] || "fa-bell";
    },
    getAccentColor: (color) => {
      const accents = {
        success: "bg-success",
        danger: "bg-danger",
        warning: "bg-warning",
        info: "bg-info",
      };
      return accents[color] || "bg-secondary";
    },
  },
};
</script>
<style scoped>
/* Prettier MOO toast — kept OFF in kiosk mode (App.vue tags the root
   with .app-kiosk-mode there) so kiosk keeps its existing plain look.
   New elements (icon bubble, bottom accent bar) are hidden by default
   and only switched on inside the MOO ancestor, so kiosk's DOM stays
   visually identical even though the markup is now shared. */
.toast-moo-icon,
.toast-moo-bar {
  display: none;
}

.h-100:not(.app-kiosk-mode) .toast {
  position: relative;
  background: #ffffff !important;
  border: none;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(25, 28, 30, 0.14);
  font-family: "Plus Jakarta Sans", "Manrope", system-ui, sans-serif;
}

.h-100:not(.app-kiosk-mode) .toast .toast-header {
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
}

.h-100:not(.app-kiosk-mode) .toast .toast-moo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 9999px;
  color: #ffffff;
  font-size: 0.7rem;
}

.h-100:not(.app-kiosk-mode) .toast .toast-moo-body {
  padding: 0;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.3;
  color: #1a1c1a !important;
}

.h-100:not(.app-kiosk-mode) .toast .toast-moo-close {
  opacity: 0.5;
  font-size: 0.8rem;
  color: #1a1c1a !important;
}

.h-100:not(.app-kiosk-mode) .toast .toast-moo-bar {
  display: block;
  height: 3px;
  width: 100%;
}

/* KSK037 — P20 kiosk toast: white pill, 72px, icon bubble + round close.
   `.h-100.app-kiosk-mode` matches the App root in kiosk; it is declared after
   the MOO rules (same specificity) so it wins where an inner `.h-100` would
   otherwise let the MOO card style leak into kiosk. */
.h-100.app-kiosk-mode .toast {
  position: relative;
  width: auto;
  min-width: 520px;
  max-width: 920px;
  margin-top: 0 !important;
  background: var(--ksk-surface) !important;
  border: none;
  border-radius: var(--ksk-radius-pill);
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(20, 26, 20, 0.18);
  font-family: var(--ksk-font);
}

.h-100.app-kiosk-mode .toast .toast-header {
  min-height: 72px;
  align-items: center;
  gap: var(--ksk-space-2);
  padding: 8px;
}

.h-100.app-kiosk-mode .toast .toast-moo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 50%;
  font-size: 24px;
  color: var(--ksk-on-primary);
}

.h-100.app-kiosk-mode .toast .toast-moo-icon.bg-success {
  background: var(--ksk-primary) !important;
}

.h-100.app-kiosk-mode .toast .toast-moo-icon.bg-danger {
  background: color-mix(in srgb, var(--ksk-error) 12%, #ffffff) !important;
  color: var(--ksk-error);
}

.h-100.app-kiosk-mode .toast .toast-moo-body {
  flex: 1;
  min-width: 0;
  padding: 0;
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 600;
  color: var(--ksk-text) !important;
}

.h-100.app-kiosk-mode .toast .toast-moo-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  margin-left: 0 !important;
  border-radius: 50%;
  background: var(--ksk-surface-muted);
  font-size: 22px !important;
  opacity: 1;
  color: var(--ksk-text-secondary) !important;
}

.h-100.app-kiosk-mode .toast .toast-moo-bar {
  display: none;
}
</style>
